import { FunctionComponent, memo, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Kanban, TreeFilterGroup, TreeFilterItem } from 'plume-ui';

import uuid from 'react-uuid';
import FilterConfigWrapper from '../FilterConfigWrapper';
import FilterOperatorToggler from '../renderers/FilterOperatorToggler';
import { CriteriaType, Operator, SubclauseType } from '../types';
import IssueFilterItem from './IssueFilterItem';
import { getDefaultCriteriaDisplayValue } from './helpers';
import {
  CriteriaDisplay,
  CriteriaDisplayWithValue,
  IssueFilterGroup,
} from './types';

type IssueFilterConfigProps = {
  items: CriteriaDisplay[];
  initialData?: IssueFilterGroup[];
  onChange?: (data: IssueFilterGroup[]) => void;
};

const IssueFilterConfig: FunctionComponent<IssueFilterConfigProps> = ({
  items,
  onChange,
  initialData,
}) => {
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState<string>();
  const [groups, setGroups] = useState<IssueFilterGroup[]>([
    {
      id: '0',
      name: 'New Group',
      items: [],
      operator: Operator.AND,
      externalOperator: Operator.AND,
    },
  ]);

  const [loadedInitialData, setLoadedInitialData] = useState<boolean>(false);

  const selectedItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const isChecked = (item: CriteriaDisplay) =>
    selectedItems?.findIndex(
      (it) => it.displayName === item.displayName || it.key === item.key,
    ) >= 0;

  const filteredItems = useMemo(() => {
    if (!items) return [];
    return items
      .flatMap((category) => category.subItems)
      .filter((item) =>
        item?.displayName
          .toLowerCase()
          .includes((searchQuery || '').toLowerCase()),
      )
      .filter((item): item is CriteriaDisplay => !!item);
  }, [items, searchQuery]);

  const mapCriteriaDisplayToTreeItem = (item: CriteriaDisplay) => {
    return (
      <TreeFilterItem
        key={item.displayName}
        onClick={() => handleSelectItem(item)}
        info=""
        isChecked={isChecked(item)}
        label={item.displayName}
        variant="checkbox"
      />
    );
  };

  const mapIssueCategoryToTree = (category: CriteriaDisplay) => {
    if (category.criteriaType === CriteriaType.HealthScore)
      return (
        <TreeFilterItem
          key={`category-${category.key}`}
          label={category.displayName}
          info=""
          variant="checkbox"
          isChecked={isChecked(category)}
          onClick={() => handleSelectItem(category)}
        />
      );
    return (
      <TreeFilterGroup
        key={`category-${category.key}`}
        title={category.displayName}
        variant="checkbox"
        isChecked={isChecked(category)}
        onGroupChecked={() => handleSelectItem(category)}
      >
        {category.subItems &&
          category.subItems.map((filt) => mapCriteriaDisplayToTreeItem(filt))}
      </TreeFilterGroup>
    );
  };

  const getFilteredGroups = (groupsToFilter: IssueFilterGroup[]) => {
    const filtered = groupsToFilter.filter((group) => group.items.length > 0);
    const newGroupsWithEmpty = [
      ...filtered,
      {
        id: uuid(),
        name: 'New Group',
        items: [],
        operator: Operator.AND,
        externalOperator: Operator.AND,
      },
    ] as IssueFilterGroup[];
    return newGroupsWithEmpty;
  };

  const handleSelectItem = (item: CriteriaDisplay) => {
    const newItem = {
      ...item,
      selectedValue: getDefaultCriteriaDisplayValue(item),
      operator: item.allowedOperators[0].key || '=',
      subCriterias: [],
      uid: uuid(),
    };
    const newGroups = [...groups];
    if (
      newGroups.length &&
      newGroups[newGroups.length - 1].items.length === 0
    ) {
      newGroups[newGroups.length - 1].items.push(newItem);
    } else {
      newGroups.push({
        name: 'New Group',
        id: uuid(),
        items: [newItem],
        operator: Operator.AND,
        externalOperator: Operator.AND,
      });
    }
    const filtered = getFilteredGroups(newGroups);
    setGroups(filtered);
    onChange?.(filtered);
  };

  const handleDeleteItem = (item: CriteriaDisplayWithValue) => {
    const newGroups = [...groups];
    newGroups.forEach((group) => {
      const itemIndex = group.items.findIndex((it) => it === item);
      if (itemIndex >= 0) {
        group.items.splice(itemIndex, 1);
      }
    });

    const filtered = getFilteredGroups(newGroups);
    setGroups(filtered);
    onChange?.(filtered);
  };

  const handleChangeItem = (
    newValue: SubclauseType | number | string,
    item: typeof groups[number]['items'][number],
  ) => {
    const newGroups = [...groups];
    const groupWithItem = newGroups.find((group) => group.items.includes(item));
    if (groupWithItem) {
      const itemIndex = groupWithItem.items.findIndex((it) => it === item);
      if (typeof newValue === 'object') {
        groupWithItem.items[itemIndex].subCriterias = [newValue];
      } else {
        groupWithItem.items[itemIndex].selectedValue = newValue;
      }
    }
    setGroups(newGroups);
    onChange?.(newGroups);
  };

  const handleChangeItemOperator = (
    operator: string,
    item: CriteriaDisplayWithValue,
  ) => {
    const newGroups = [...groups];
    newGroups.forEach((group) => {
      const itemIndex = group.items.findIndex((it) => it === item);
      if (itemIndex >= 0) {
        group.items[itemIndex].operator = operator;
      }
    });
    setGroups(newGroups);
    onChange?.(newGroups);
  };

  const handleChangeItemSubItem = (
    subItem: string | undefined,
    item: CriteriaDisplayWithValue,
  ) => {
    const newGroups = [...groups];
    newGroups.forEach((group) => {
      const itemIndex = group.items.findIndex((it) => it === item);
      if (itemIndex >= 0) {
        if (subItem) {
          group.items[itemIndex].selectedSubItem = subItem;
          group.items[itemIndex].selectedValue = subItem;
        } else {
          group.items[itemIndex].selectedSubItem = undefined;
          group.items[itemIndex].selectedValue = group.items[itemIndex].key;
        }
      }
    });
    setGroups(newGroups);
    onChange?.(newGroups);
  };

  const handleChangeGroups = (newGroups: IssueFilterGroup[]) => {
    const filtered = getFilteredGroups(newGroups);
    setGroups(filtered);
    onChange?.(filtered);
  };

  const handleChangeGroupOperator = (
    operator: Operator,
    groupIndex: number,
  ) => {
    const newGroups = [...groups];
    if (groupIndex >= 0) {
      newGroups[groupIndex].operator = operator;
      setGroups(newGroups);
      onChange?.(newGroups);
    }
  };

  const handleChangeGroupExternalOperator = (
    operator: Operator,
    groupIndex: number,
  ) => {
    const newGroups = [...groups];
    if (groupIndex >= 0) {
      newGroups[groupIndex].externalOperator = operator;
      setGroups(newGroups);
      onChange?.(newGroups);
    }
  };

  // ! inital data (uncontrolled) should load into state only once
  useEffect(() => {
    if (initialData && !loadedInitialData) {
      if (initialData.length) setGroups(getFilteredGroups(initialData));
      setLoadedInitialData(true);
    }
  }, [initialData, loadedInitialData]);

  return (
    <FilterConfigWrapper
      onSearch={setSearchQuery}
      noSelectionMessage={t('monitorAdd.pleaseSelectAttribute')}
      sectionTitle={t('monitorAdd.monitorEvents.title')}
      tableClassName="ConfigTable-issueFilter"
      searchPlaceholder={t('monitorAdd.filterSearch.events')}
      definitionTooltip={t('monitorAdd.booleanTooltip')}
      selectedFilters={
        <Kanban
          groups={groups}
          onChange={handleChangeGroups}
          direction="vertical"
          className="ConfigTable__groupContainer"
          groupRender={({ group, groupIndex, content }) => (
            <>
              <div className="FilterGroup">
                <div className="FilterGroup__header">
                  <FilterOperatorToggler
                    operator={group.operator}
                    onToggle={(operator) =>
                      handleChangeGroupOperator(operator, groupIndex)
                    }
                  />
                </div>
                <div className="FilterGroup__content">
                  {group.items.length ? (
                    content
                  ) : (
                    <div className="FilterGroup__noItemsIndicator">
                      {t('monitorAdd.pleaseSelectAttribute')}
                    </div>
                  )}
                </div>
              </div>
              {groupIndex < groups.length - 1 && (
                <div className="FilterGroup__externalOperator">
                  <FilterOperatorToggler
                    onToggle={(operator) =>
                      handleChangeGroupExternalOperator(operator, groupIndex)
                    }
                    operator={group.externalOperator}
                  />
                </div>
              )}
            </>
          )}
          itemKey={(item) => item.uid}
          itemRender={({ item, itemIndex, group, Handle }) => (
            <>
              <IssueFilterItem
                key={`${item.key}`}
                allowedOperators={item.allowedOperators}
                filter={item}
                onChange={(data) => handleChangeItem(data, item)}
                onSelectSubItem={(subItem) =>
                  handleChangeItemSubItem(subItem, item)
                }
                onChangeOperator={(operator) =>
                  handleChangeItemOperator(operator, item)
                }
                onDelete={() => handleDeleteItem(item)}
                Handle={Handle}
              />
              {itemIndex < group.items.length - 1 && (
                <div className="FilterItem__groupOperator">
                  {group.operator}
                </div>
              )}
            </>
          )}
        />
      }
      filterSelectorContent={
        <>
          {!Boolean(searchQuery?.length) && (
            <>{items.map((category) => mapIssueCategoryToTree(category))}</>
          )}
          {Boolean(searchQuery?.length) &&
            filteredItems.map((filter) => mapCriteriaDisplayToTreeItem(filter))}
          {Boolean(searchQuery?.length) && !Boolean(filteredItems.length) && (
            <TreeFilterItem key={'noResults'} info="" label={t('noResults')} />
          )}
        </>
      }
    />
  );
};

export default memo(IssueFilterConfig);
